import React, {useEffect, useMemo, useState} from 'react';
import useApi from "../../../utils/useApi";
import {Link, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {Eye, EyeOff, X} from "lucide-react";
import DependentFields from "./DependentFields";
import ClientActions from "../ClientActions/ClientActions";
import {validationRules, formatters} from "../../../utils/formFormatAndValidate";
import Swal from "sweetalert2";
import {states} from "../../../utils/states";
import PrintableForm from "../../PrintableForm/PrintableForm";
import DateInput from "../../UI/DateInput";
import SelectTaxYear from "../SelectTaxYear/SelectTaxYear";
import PortalActions from "../ClientActions/PortalActions";

const ClientForm = ({ handleShowHeader, handleShowSettingsHeader, mode = 'create' }) => {
    const { id } = useParams();
    const [print, setPrint] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const { getClientEdit, getPreparers, updateProfile, createClient, setClientPassword } = useApi();
    const [client, setClient] = useState(null);
    const [availableYears, setAvailableYears] = useState([]);

    const [preparers, setPreparers] = useState(null);
    const [formData, setFormData] = useState([]);
    const [visiblePasswords, setVisiblePasswords] = useState({});
    const [isDark, setIsDark] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const navigate = useNavigate();

    // Get current year for the year selector range
    const currentYear = new Date().getFullYear();
    // Default to previous tax year (current year - 1)
    const defaultYear = (currentYear - 1).toString();
    const [selectedYear, setSelectedYear] = useState(searchParams.get('year') ?? defaultYear);

    let preparerOptions = [
        {
            value: '',
            label: 'Please select preparer'
        }
    ];

    useEffect(() => {
        handleShowHeader(true);
        handleShowSettingsHeader(false);
        // Check system preference for dark mode
        if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
            setIsDark(true);
        }

        window.scrollTo(0, 0);

    }, []);

    useEffect(() => {
        fetchData();
    }, [id, mode, selectedYear]);

    const fetchData = async () => {
        try {
            setIsLoading(true);
            let data = {
                year: selectedYear,
            }

            if (mode === 'update') {

                const response = await getClientEdit(id, data);
                setClient(response.client);
                setPreparers(response.preparers || []);
                setAvailableYears(response.availableYears);

                // Initialize form data after client data is available
                if (response.client) {
                    initializeFormData(response.client);
                    setPreparers(response.preparers);
                }
            } else {
                // For create mode, initialize with empty data
                const emptyClient = {
                    profile: { account_type: 'personal' },
                    client_progress: {},
                    user_type: 'client'
                };
                setClient(emptyClient);
                const preparersResponse = await getPreparers(); // You'll need to add this API endpoint
                initializeFormData(emptyClient);
                setPreparers(preparersResponse.preparers);
            }
        }
        catch (err) {
            setError(err.message);
        }
        finally {
            setIsLoading(false);
        }
    };


    const handleYearChange = (e) => {
        const newYear = e.target.value;
        setSelectedYear(newYear);
        setSearchParams({ year: newYear });
    };

    const handlePrintClose = () => {
        setPrint(false);
    }

    const handlePrint = () => {
        setPrint(true);
    }

    const handleSetClientPassword = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        const value = formData.set_password;
        const data = {
            password: value,
        }
        const response = setClientPassword(client.id, data);

        try {
            if (response.success) {
                setIsSaving(false);
                await Swal.fire({
                                    title: 'Success!',
                                    text: response.message,
                                    icon: 'success',
                                    timer: 2000,
                                    showConfirmButton: true
                                });

                e.target.value = '';
            }
            setIsSaving(false);
        } catch (error){
            setIsSaving(false);
            Swal.fire({
                          title: 'Error!',
                          text: error.message,
                          icon: 'error',
                          showConfirmButton: true
                      });
        }

    }

    // Modified handleInputChange function
    const handleInputChange = (e) => {
        const { name, value, type, selectionStart } = e.target;
        let formattedValue = value;
        let cursorPosition = selectionStart;

        // Find the field definition
        let field = null;
        for (const section in formFields) {
            if (Array.isArray(formFields[section])) {
                const foundField = formFields[section].find(f => f.id === name);
                if (foundField) {
                    field = foundField;
                    break;
                }
            }
        }

        if (field?.formatter) {
            const previousValue = formData[name] || '';
            const previousLength = previousValue.length;

            formattedValue = formatters[field.formatter](value);

            // Adjust cursor position after formatting
            if (cursorPosition) {
                // If deleting, move cursor back
                if (formattedValue.length < previousLength) {
                    if (previousValue[cursorPosition - 1] === '-') {
                        cursorPosition--;
                    }
                }
                // If typing, move cursor forward after a dash
                else if (formattedValue.length > previousLength) {
                    if (formattedValue[cursorPosition] === '-') {
                        cursorPosition++;
                        cursorPosition++;
                    }
                }

            }
        }

        // Update form data
        setFormData(prev => ({
            ...prev,
            [name]: formattedValue
        }));

        // Validate the field
        validateField(name, formattedValue, field);
    };

    // Add validation function
    const validateField = (name, value, field) => {
        let errors = { ...validationErrors };

        // Clear existing errors for this field
        delete errors[name];

        // Required field validation
        if (field?.required && !value) {
            errors[name] = 'This field is required';
        }

        // Pattern validation
        if (field?.formatter && validationRules[field.formatter]) {
            const rule = validationRules[field.formatter];
            if (value && !rule.pattern.test(value)) {
                errors[name] = rule.message;
            }
        }

        setValidationErrors(errors);
    };

    const initializeFormData = (clientData) => {
        let initialData = {
            // Year
            year: clientData.client_progress?.year || '',
            // Basic Information
            client: clientData.profile?.client || '',
            account_type: clientData.profile?.account_type || '',
            client_id: clientData.client_id || '',
            contact_primary: clientData.profile?.contact_primary || '',
            user_client_email: clientData.user_client_email || '',
            client_phone_primary: clientData.user_client_phone_primary || '',
            email: clientData.email || '',
            phone_primary: clientData.profile?.phone_primary || '',
            phone_secondary: clientData.profile?.phone_secondary || '',
            ssn_id: clientData.profile?.ssn_id || '',
            address: clientData.profile?.address || '',
            occupation: clientData.profile?.occupation || '',

            // Filing Status
            filing_status: clientData.client_progress?.filing_status || clientData.profile?.filling_status,
            spouse_dod: clientData.profile?.spouse_dod || '',

            // Spouse Information
            spouse_name: clientData.profile?.spouse_name || '',
            spouse_ssn: clientData.profile?.spouse_ssn || '',
            spouse_email: clientData.profile?.spouse_email || '',
            spouse_occupation: clientData.profile?.spouse_occupation || '',

            // Dependent Information
            dependents: clientData.profile?.dependents || '',
            dep_name: clientData.profile?.dep_name || [],
            dep_rel: clientData.profile?.dep_rel || [],
            dep_dob: clientData.profile?.dep_dob || [],
            dep_ssn: clientData.profile?.dep_ssn || [],

            // State Return
            state_return: clientData.profile?.state_return || '',
            st_return: clientData.profile?.st_return || '',

            // Bank Information
            rt_number: clientData.profile?.rt_number || '',
            act_number: clientData.profile?.act_number || '',
            bank_name: clientData.profile?.bank_name || '',

            // Client Status
            new_client: clientData.client_progress?.new_client > 0 ? 1 : (clientData.client_progress?.new_client === 0 ? 0 : false),
            hear_aboutus: clientData.profile?.hear_aboutus || '',
            referral: clientData.profile?.referral || '',

            // Progress Information
            tax_started: clientData.client_progress?.tax_started || false,
            tax_documents_received_method: clientData.client_progress?.tax_documents_received_method || '',
            tax_document_preparer_initials: clientData.client_progress?.tax_document_preparer_initials || '',
            tax_incomplete: clientData.client_progress?.tax_incomplete || false,
            tax_incomplete_notes: clientData.client_progress?.tax_incomplete_notes || '',
            ready_for_preparer: clientData.client_progress?.ready_for_preparer || false,
            ready_for_preparer_notes: clientData.client_progress?.ready_for_preparer_notes || '',

            // Appointment Information
            appointment_scheduled: clientData.client_progress?.appointment_scheduled || '',
            appointment_email_confirmation_sent: clientData.client_progress?.appointment_email_confirmation_sent || '',
            appointment_email_confirmation_initials: clientData.client_progress?.appointment_email_confirmation_initials || '',

            // Tax Preparer Information
            preferred_preparer: clientData.client_progress?.preferred_preparer || '',
            tax_preparer: clientData.client_progress?.tax_preparer?.id || '',
            refund_amount: clientData.client_progress?.refund_amount || '',
            state_refund_amount: clientData.client_progress?.state_refund_amount || '',
            tax_prep_fee: clientData.client_progress?.tax_prep_fee || '',

            // Additional Fees
            has_additional_fees: clientData.client_progress?.has_additional_fees || '',
            addt_fees: clientData.client_progress?.addt_fees || '',

            // Credit Card Fee
            has_credit_card_fee: clientData.client_progress?.has_credit_card_fee || '',
            credit_card_fee: clientData.client_progress?.credit_card_fee || '',

            // Abatement
            has_abatement_fees: clientData.client_progress?.has_abatement_fees || '',
            abatement_fee: clientData.client_progress?.abatement_fee || '',
            abatement_initials: clientData.client_progress?.abatement_initials || '',

            // Bookkeeping
            has_bookkeeping_fees: clientData.client_progress?.has_bookkeeping_fees || '',
            bookkeeping_fee: clientData.client_progress?.bookkeeping_fee || '',
            bookkeeping_fee_initials: clientData.client_progress?.bookkeeping_fee_initials || '',

            // Return Processing
            document_copies: clientData.client_progress?.document_copies || '',
            picked_up_return: clientData.client_progress?.picked_up_return || false,
            office_pick_up_initials: clientData.client_progress?.office_pick_up_initials || '',
            initials: clientData.client_progress?.initials || '',

            // Extension Information
            extension_needed: clientData.client_progress?.extension_needed || false,
            extension_initials: clientData.client_progress?.extension_initials || '',
            extension_fee: clientData.client_progress?.extension_fee || '',
            extension_notes: clientData.client_progress?.extension_notes || '',

            // Amendment Information
            amendment_needed: clientData.client_progress?.amendment_needed || false,
            amendment_needed_year: clientData.client_progress?.amendment_needed_year || false,
            amendment_initials: clientData.client_progress?.amendment_initials || '',
            amendment_fee: clientData.client_progress?.amendment_fee || '',
            amendment_notes: clientData.client_progress?.amendment_notes || '',

            // Prior Return Information
            prior_year_return_needed: clientData.client_progress?.prior_year_return_needed || false,
            prior_return_needed_year: clientData.client_progress?.prior_return_needed_year || false,
            prior_return_initials: clientData.client_progress?.prior_return_initials || '',
            prior_return_fee: clientData.client_progress?.prior_return_fee || '',
            prior_return_notes: clientData.client_progress?.prior_return_notes || '',

            // Software and LLC
            software_used: clientData.client_progress?.software_used || '',
            return_includes_llc: clientData.client_progress?.return_includes_llc || false,
            has_business: clientData.client_progress?.has_business || false,
            business_name: clientData.client_progress?.business_name || '',
            franchise_report: clientData.client_progress?.franchise_report || false,
            franchise_fee: clientData.client_progress?.franchise_fee || '',
            franchise_report_initials: clientData.client_progress?.franchise_report_initials || '',
            franchise_notes: clientData.client_progress?.franchise_notes || '',

            // Payment Information
            payment_method: clientData.client_progress?.payment_method || '',
            payment_made: clientData.client_progress?.payment_made || false,
            tax_complete: clientData.client_progress?.tax_complete || false,
            tax_complete_initials: clientData.client_progress?.tax_complete_initials || '',

            // Additional Notes
            note: clientData.client_progress?.note || '',

            // Format all date fields
            client_dob: formatDateForInput(clientData.profile?.client_dob),
            spouse_dob: formatDateForInput(clientData.profile?.spouse_dob),
            tax_started_date: formatDateForInput(clientData.client_progress?.tax_started_date),
            tax_incomplete_date: formatDateForInput(clientData.client_progress?.tax_incomplete_date),
            ready_for_preparer_date: formatDateForInput(clientData.client_progress?.ready_for_preparer_date),
            date_return: formatDateForInput(clientData.client_progress?.date_return),
            amendment_filed_date: formatDateForInput(clientData.client_progress?.amendment_filed_date),
            abatement_filed_date: formatDateForInput(clientData.client_progress?.abatement_filed_date),
            appointment_date: formatDateForInput(clientData.client_progress?.appointment_date),
            appointment_email_confirmation_date: formatDateForInput(clientData.client_progress?.appointment_email_confirmation_date),

            bookkeeping_fee_filed_date: formatDateForInput(clientData.client_progress?.bookkeeping_fee_filed_date),
            prior_return_filed_date: formatDateForInput(clientData.client_progress?.prior_return_filed_date),
            irs_acceptance_date: formatDateForInput(clientData.client_progress?.irs_acceptance_date),
            picked_up_return_date: formatDateForInput(clientData.client_progress?.picked_up_return_date),
            documents_uploaded_date: formatDateForInput(clientData.client_progress?.documents_uploaded_date),
            extension_filed_date: formatDateForInput(clientData.client_progress?.extension_filed_date),
            franchise_report_filed_date: formatDateForInput(clientData.client_progress?.franchise_report_filed_date),
            payment_made_date: formatDateForInput(clientData.client_progress?.payment_made_date),
            tax_complete_date: formatDateForInput(clientData.client_progress?.tax_complete_date),
        };

        initialData = setDependents(clientData.profile, initialData);

        console.log({ initialData });

        setFormData(initialData);




    };

    const setDependents = (data, initialData) => {
        for (let x = 1; x <= 10; x++) {
            let dependent = {};
            console.log(data);
            console.log(`dep${x}_name`);
            console.log(data[`dep${x}_name`]);

            if (data[`dep${x}_name`]) {
                initialData[`dep${x}_name`] = data[`dep${x}_name`];
                initialData[`dep${x}_dob`] = data[`dep${x}_dob`];
                initialData[`dep${x}_rel`] = data[`dep${x}_rel`];
                initialData[`dep${x}_ssn`] = data[`dep${x}_ssn`];
            }
        }

        return initialData;
    }


    const formFields = useMemo(() => {
        if (!client || !preparers) return {};

        const preparerOptions = [
            { value: '', label: 'Please select preparer' },
            ...preparers.map((preparer) => ({
                value: preparer.id,
                label: preparer.profile.client,
            })),
        ];
        return {

            basicInformation: [
                {
                    id: 'client',
                    label: client.profile.account_type === 'personal' ? 'Client Name' : 'Legal Entity Name',
                    type: 'text',
                    required: true,
                    placeholder: client.profile.account_type === 'personal' ? 'Please enter your client name' : 'Please enter the legal name of your business'
                },
                {
                    id: 'account_type',
                    label: 'Account Type',
                    type: 'select',
                    required: true,
                    options: [
                        { value: '', label: 'Select Account Type' },
                        { value: 'personal', label: 'Personal' },
                        { value: 'business', label: 'Business' }
                    ]
                },
                {
                    id: 'occupation',
                    label: 'Type of Business',
                    type: 'select',
                    required: true,
                    showWhen: (formData) => formData.account_type === 'business',
                    options: [
                        { value: '', label: 'Select' },
                        { value: 'Individual/Sole proprietor or Single-member LLC', label: 'Individual/Sole proprietor or Single-member LLC' },
                        { value: 'Corporation', label: 'Corporation' },
                        { value: 'Partnership', label: 'Partnership' },
                        { value: 'Limited Partnership', label: 'Limited Partnership' },
                        { value: 'Limited Liability Corporation', label: 'Limited Liability Corporation' },
                        { value: 'Trustee/estate', label: 'Trustee/estate' },
                        { value: 'Nonprofit Organization', label: 'Nonprofit Organization' },
                        { value: 'Other', label: 'Other' }
                    ]
                },
                {
                    id: 'client_id',
                    label: 'Client ID',
                    type: 'text',
                    required: true,
                    placeholder: 'Choose ID'
                },
                {
                    id: 'set_password',
                    label: 'Set New Password For Client',
                    type: 'setPassword',
                    required: false,
                    placeholder: 'Set New Password'
                },

                {
                    id: 'contact_primary',
                    label: 'Primary Contact',
                    type: 'text',
                    required: true,
                    showWhen: (formData) => formData.account_type === 'business'
                }
            ],

            contactInformation: [
                {
                    id: 'email',
                    label: `${client.user_type === 'account' ? 'Account' : 'Client'} Email`,
                    type: 'email',
                    required: true
                },
                {
                    id: 'phone_primary',
                    label: `${client.user_type === 'account' ? 'Account' : 'Client'} Phone`,
                    type: 'tel',
                    required: true,
                    minLength: 10,
                    formatter: 'phone'
                },
                {
                    id: 'phone_secondary',
                    label: 'Alternate Phone',
                    type: 'tel',
                    required: false,
                    minLength: 10,
                    formatter: 'phone'
                }
            ],

            personalInformation: [
                {
                    id: 'client_dob',
                    label: 'Date of Birth',
                    type: 'date',
                    required: true,
                    showWhen: (formData) => formData.account_type === 'personal'
                },
                {
                    id: 'ssn_id',
                    label: 'SSN / Tax ID',
                    type: 'password',
                    required: true,
                    minLength: 9,
                    formatter: 'ssn',
                    showPasswordToggle: true
                },
                {
                    id: 'address',
                    label: 'Mailing Address',
                    type: 'textarea',
                    required: true,
                    rows: 3
                },

            ],

            filingStatus: [
                {
                    id: 'filing_status',
                    label: 'Filing Status',
                    type: 'select',
                    required: true,
                    showWhen: (formData) => formData.account_type === 'personal',
                    options: [
                        { value: '', label: 'Select Filing Status' },
                        { value: 'Single', label: 'Single' },
                        { value: 'Head of Household', label: 'Head of Household' },
                        { value: 'Married Filing Joint Return', label: 'Married Filing Joint Return' },
                        { value: 'Married Filing Seperately', label: 'Married Filing Seperately' },
                        { value: 'Qualifying Widow', label: 'Qualifying Widow' }
                    ]
                },
                {
                    id: 'spouse_dod',
                    label: "Spouse's date of death",
                    type: 'date',
                    showWhen: (formData) => formData.filing_status === 'Qualifying Widow'
                }
            ],

            spouseInformation: [
                {
                    id: 'spouse_name',
                    label: "Spouse's Name",
                    type: 'text',
                    showWhen: (formData) => ['Married Filing Joint Return', 'Married Filing Seperately'].includes(formData.filing_status)
                },
                {
                    id: 'spouse_dob',
                    label: 'Spouse DOB',
                    type: 'date',
                    showWhen: (formData) => ['Married Filing Joint Return', 'Married Filing Seperately'].includes(formData.filing_status)
                },
                {
                    id: 'spouse_ssn',
                    label: 'Spouse SSN',
                    type: 'password',
                    showPasswordToggle: true,
                    formatter: 'ssn',
                    showWhen: (formData) => ['Married Filing Joint Return', 'Married Filing Seperately'].includes(formData.filing_status)
                },
                {
                    id: 'spouse_email',
                    label: 'Spouse Email',
                    type: 'email',
                    showWhen: (formData) => ['Married Filing Joint Return', 'Married Filing Seperately'].includes(formData.filing_status)
                },
                {
                    id: 'spouse_occupation',
                    label: 'Spouse Occupation',
                    type: 'text',
                    showWhen: (formData) => ['Married Filing Joint Return', 'Married Filing Seperately'].includes(formData.filing_status)
                }
            ],

            dependentInformation: [
                {
                    id: 'dependents',
                    label: 'Do you have dependents?',
                    type: 'select',
                    required: true,
                    showWhen: (formData) => formData.account_type === 'personal',
                    options: [
                        { value: '', label: 'Please Select' },
                        { value: 'have_dependents', label: 'Yes' },
                        { value: 'no', label: 'No' }
                    ]
                }
            ],

            dependentFields: {
                template: [
                    {
                        id: 'dep_name',
                        label: 'Name',
                        type: 'text',
                        required: true,
                        placeholder: 'Enter Dependent Name'
                    },
                    {
                        id: 'dep_rel',
                        label: 'Relationship',
                        type: 'text',
                        required: true,
                        placeholder: 'Enter Dependent Relationship'
                    },
                    {
                        id: 'dep_dob',
                        label: 'DOB',
                        type: 'date',
                        required: true
                    },
                    {
                        id: 'dep_ssn',
                        label: 'SSN',
                        type: 'password',
                        required: true,
                        showPasswordToggle: true,
                        formatter: 'ssn',
                        minLength: 9
                    }
                ]
            },

            stateReturn: [
                {
                    id: 'state_return',
                    label: 'Will you be filing a state return?',
                    type: 'select',
                    required: true,
                    showWhen: (formData) => formData.account_type === 'personal',
                    options: [
                        { value: '', label: 'Please Select' },
                        { value: 'yes_state_return', label: 'Yes' },
                        { value: 'No', label: 'No' }
                    ]
                },
                {
                    id: 'st_return',
                    label: 'Select State',
                    type: 'select',
                    showWhen: (formData) => formData.state_return === 'yes_state_return',
                    options: states
                }
            ],

            bankInformation: [
                {
                    id: 'rt_number',
                    label: 'Bank Routing #',
                    type: 'password',
                    showPasswordToggle: true,
                    minLength: 9,
                    maxLength: 9,
                    formatter: 'numeric'
                },
                {
                    id: 'act_number',
                    label: 'Account #',
                    type: 'password',
                    showPasswordToggle: true,
                    formatter: 'numeric'
                },
                {
                    id: 'bank_name',
                    label: 'Bank Name',
                    type: 'text'
                }
            ],

            clientStatus: [
                {
                    id: 'new_client',
                    label: 'New Client',
                    type: 'select',
                    options: [
                        { value: '', label: 'Please Select' },
                        { value: '1', label: 'Yes' },
                        { value: '0', label: 'No' }
                    ]
                },
                {
                    id: 'hear_aboutus',
                    label: 'How did you hear about us?',
                    type: 'select',
                    showWhen: (formData) => formData.new_client === '1',
                    options: [
                        { value: '', label: 'Please Select' },
                        { value: 'Facebook', label: 'Facebook' },
                        { value: 'Walk On', label: 'Walk-in' },
                        { value: 'Flyer', label: 'Flyer' },
                        { value: 'referral_hear', label: 'Referral - If so name' },
                        { value: 'Internet Search', label: 'Internet Search' },
                        { value: 'Yelp', label: 'Yelp' },
                        { value: 'Thumbtack', label: 'Thumbtack' }
                    ]
                }
            ],

            progressInformation: [
                {
                    id: 'tax_started',
                    label: 'Tax Return Documents Received',
                    type: 'checkbox',
                    customClass: 'md:col-span-2'
                },
                {
                    id: 'tax_documents_received_method',
                    label: 'How Received',
                    type: 'select',
                    options: [
                        { value: '', label: 'Select How Received' },
                        { value: 'portal_upload', label: 'Portal Upload' },
                        { value: 'in_office_apt', label: 'In Office Appointment' },
                        { value: 'drop_off', label: 'Drop Off Service' }
                    ],
                    showWhen: (formData) => formData.tax_started

                },
                {
                    id: 'tax_started_date',
                    label: 'Date',
                    type: 'date',
                    showWhen: (formData) => formData.tax_started
                },
                {
                    id: 'tax_document_preparer_initials',
                    label: 'Admin Initials',
                    type: 'text',
                    maxLength: 3,
                    showWhen: (formData) => formData.tax_started
                },
                {
                    id: 'tax_incomplete',
                    label: 'Tax Return Incomplete',
                    type: 'checkbox',
                    customClass: 'md:col-span-2'
                },
                {
                    id: 'tax_incomplete_date',
                    label: 'Incomplete Date',
                    type: 'date',
                    showWhen: (formData) => formData.tax_incomplete
                },
                {
                    id: 'tax_incomplete_notes',
                    label: 'Notes',
                    type: 'text',
                    placeholder: 'Notes...',
                    showWhen: (formData) => formData.tax_incomplete
                },
                {
                    id: 'ready_for_preparer',
                    label: 'Ready for Preparer',
                    type: 'checkbox',
                    customClass: 'md:col-span-2'
                },
                {
                    id: 'ready_for_preparer_date',
                    label: 'Ready Date',
                    type: 'date',
                    showWhen: (formData) => formData.ready_for_preparer
                },
                {
                    id: 'ready_for_preparer_notes',
                    label: 'Notes',
                    type: 'text',
                    placeholder: 'Notes...',
                    showWhen: (formData) => formData.ready_for_preparer
                }
            ],

            appointmentInformation: [
                {
                    id: 'appointment_scheduled',
                    label: 'Appointment Scheduled',
                    type: 'checkbox',
                    customClass: 'md:col-span-2'
                },
                {
                    id: 'appointment_date',
                    label: 'Appointment Date',
                    type: 'date',
                    showWhen: (formData) => formData.appointment_scheduled
                },
                {
                    id: 'appointment_email_confirmation_sent',
                    label: 'Appointment Email Confirmation Sent',
                    type: 'checkbox',
                    showWhen: (formData) => formData.appointment_scheduled,
                    customClass: 'md:col-span-2'
                },
                {
                    id: 'appointment_email_confirmation_date',
                    label: 'Sent Email Confirmation Date',
                    type: 'date',
                    showWhen: (formData) => formData.appointment_email_confirmation_sent,
                },
                {
                    id: 'appointment_email_confirmation_initials',
                    label: 'Sent Email Confirmation Initials',
                    type: 'text',
                    showWhen: (formData) => formData.appointment_email_confirmation_sent
                }
            ],

            preparerInformation: [
                {
                    id: 'tax_preparer',
                    label: 'Tax Preparer',
                    type: 'select',
                    options: preparerOptions
                },

                {
                    id: 'tax_prep_fee',
                    label: 'Tax Prep Fee',
                    type: 'number',

                },
                {
                    id: 'has_additional_fees',
                    label: 'Include Additional Fees',
                    type: 'checkbox',
                    customClass: 'md:col-span-2'
                },
                {
                    id: 'addt_fees',
                    label: 'Additional Fees (other)',
                    type: 'number',
                    showWhen: (formData) => formData.has_additional_fees
                },
                {
                    id: 'has_credit_card_fee',
                    label: 'Include Credit Card Fees',
                    type: 'checkbox',
                    customClass: 'md:col-span-2'
                },
                {
                    id: 'credit_card_fee',
                    label: 'Credit Card Fee',
                    type: 'number',
                    showWhen: (formData) => formData.has_credit_card_fee
                },

                {
                    id: 'has_bookkeeping_fees',
                    label: 'Include Bookkeeping Fees',
                    type: 'checkbox',
                    customClass: 'md:col-span-2'
                },
                {
                    id: 'bookkeeping_fee_filed_date',
                    label: 'Bookkeeping Fee Filed Date',
                    type: 'date',
                    showWhen: (formData) => formData.has_bookkeeping_fees
                },
                {
                    id: 'bookkeeping_fee',
                    label: 'Bookkeeping Fee',
                    type: 'number',
                    showWhen: (formData) => formData.has_bookkeeping_fees
                },
                {
                    id: 'bookkeeping_fee_initials',
                    label: 'Bookkeeping Fee Initials',
                    type: 'text',
                    maxLength: 3,
                    showWhen: (formData) => formData.has_bookkeeping_fees
                },
                {
                    id: 'has_abatement_fees',
                    label: 'Include Abatement Fees',
                    type: 'checkbox',
                    customClass: 'md:col-span-2'
                },
                {
                    id: 'abatement_filed_date',
                    label: 'Abatement Filed Date',
                    type: 'date',
                    showWhen: (formData) => formData.has_abatement_fees
                },
                {
                    id: 'abatement_fee',
                    label: 'Abatement Fee',
                    type: 'number',
                    showWhen: (formData) => formData.has_abatement_fees
                },
                {
                    id: 'abatement_initials',
                    label: 'Abatement Initials',
                    type: 'text',
                    maxLength: 3,
                    showWhen: (formData) => formData.has_abatement_fees
                },
            ],

            additionalInformation: [
                {
                    id: 'extension_needed',
                    label: 'Extension Needed',
                    type: 'checkbox',
                    customClass: 'md:col-span-2',
                },
                {
                    id: 'extension_filed_date',
                    label: 'Extension Filed Date',
                    type: 'date',
                    showWhen: (formData) => formData.extension_needed
                },
                {
                    id: 'extension_fee',
                    label: 'Extension Fee',
                    type: 'number',
                    showWhen: (formData) => formData.extension_needed
                },
                {
                    id: 'extension_initials',
                    label: 'Extension Initials',
                    type: 'text',
                    maxLength: 3,
                    showWhen: (formData) => formData.extension_needed
                },
                {
                    id: 'extension_notes',
                    label: 'Extension Notes',
                    type: 'textarea',
                    rows: 4,
                    showWhen: (formData) => formData.extension_needed
                },
                {
                    id: 'amendment_needed',
                    label: 'Amendment Needed',
                    type: 'checkbox',
                    customClass: 'md:col-span-2'
                },
                {
                    id: 'amendment_needed_year',
                    label: 'Amendment Needed Year',
                    type: 'number',
                    showWhen: (formData) => formData.amendment_needed
                },
                {
                    id: 'amendment_filed_date',
                    label: 'Amendment Filed Date',
                    type: 'date',
                    showWhen: (formData) => formData.amendment_needed
                },
                {
                    id: 'amendment_fee',
                    label: 'Amendment Fee',
                    type: 'number',
                    showWhen: (formData) => formData.amendment_needed
                },
                {
                    id: 'amendment_initials',
                    label: 'Amendment Initials',
                    type: 'text',
                    maxLength: 3,
                    showWhen: (formData) => formData.amendment_needed
                },
                {
                    id: 'amendment_notes',
                    label: 'Amendment Notes',
                    type: 'textarea',
                    rows: 4,
                    showWhen: (formData) => formData.amendment_needed
                },
                // Prior Return Info
                {
                    id: 'prior_year_return_needed',
                    label: 'Prior Year Return Needed',
                    type: 'checkbox',
                    customClass: 'md:col-span-2'
                },
                {
                    id: 'prior_return_needed_year',
                    label: 'Prior Return Needed Year',
                    type: 'number',
                    showWhen: (formData) => formData.prior_year_return_needed
                },
                {
                    id: 'prior_return_filed_date',
                    label: 'Prior Return Filed Date',
                    type: 'date',
                    showWhen: (formData) => formData.prior_year_return_needed
                },
                {
                    id: 'prior_return_fee',
                    label: 'Amendment Fee',
                    type: 'number',
                    showWhen: (formData) => formData.prior_year_return_needed
                },
                {
                    id: 'prior_return_initials',
                    label: 'Prior Return Initials',
                    type: 'text',
                    maxLength: 3,
                    showWhen: (formData) => formData.prior_year_return_needed
                },
                {
                    id: 'prior_return_notes',
                    label: 'Prior Return Notes',
                    type: 'textarea',
                    rows: 4,
                    showWhen: (formData) => formData.prior_year_return_needed
                }
            ],

            returnProcessing: [
                {
                    id: 'date_return',
                    label: 'Date Return Submitted',
                    type: 'date'
                },
                {
                    id: 'irs_acceptance_date',
                    label: 'IRS Acceptance Date',
                    type: 'date'
                },
                {
                    id: 'refund_amount',
                    label: 'Refund Amount',
                    type: 'number',

                },
                {
                    id: 'state_refund_amount',
                    label: 'State Refund Amount',
                    type: 'number',

                },
                {
                    id: 'document_copies',
                    label: 'How would you like to receive your document copies',
                    type: 'select',
                    options: [
                        { value: '', label: 'Select' },
                        { value: 'Office Pick Up', label: 'Office Pick Up' },
                        { value: 'Portal Upload', label: 'Portal Upload' }
                    ]
                },
                {
                    id: 'picked_up_return',
                    label: 'Picked up return?',
                    type: 'checkbox',
                    showWhen: (formData) => formData.document_copies === 'Office Pick Up'
                },
                {
                    id: 'picked_up_return_date',
                    label: 'Pick Up Date',
                    type: 'date',
                    showWhen: (formData) => formData.document_copies === 'Office Pick Up' && formData.picked_up_return
                },
                {
                    id: 'office_pick_up_initials',
                    label: 'Initials',
                    type: 'text',
                    maxLength: 3,
                    showWhen: (formData) => formData.document_copies === 'Office Pick Up' && formData.picked_up_return
                },
                {
                    id: 'documents_uploaded_date',
                    label: 'Date documents uploaded',
                    type: 'date',
                    showWhen: (formData) => formData.document_copies === 'Portal Upload'
                },
                {
                    id: 'initials',
                    label: 'Initials',
                    type: 'text',
                    maxLength: 3,
                    showWhen: (formData) => formData.document_copies === 'Portal Upload'
                }
            ],

            businessInformation: [
                {
                    id: 'has_business',
                    label: 'Does this client have a business return we process?',
                    type: 'checkbox',
                    customClass: 'md:col-span-2'
                },
                {
                    id: 'business_name',
                    label: 'Business Name',
                    type: 'text',
                    customClass: 'md:col-span-2',
                    showWhen: (formData) => formData.has_business,
                },

                {
                    id: 'return_includes_llc',
                    label: 'Does this return include an LLC?',
                    type: 'select',
                    options: [
                        { value: '', label: 'Please Select' },
                        { value: '1', label: 'Yes' },
                        { value: '0', label: 'No' }
                    ],
                    customClass: 'md:col-span-2'
                },
                {
                    id: 'franchise_report',
                    label: 'Is a TX Franchise Report Needed?',
                    type: 'checkbox',
                    showWhen: (formData) => formData.return_includes_llc === '1',
                    customClass: 'md:col-span-2',
                },

                {
                    id: 'franchise_report_filed_date',
                    label: 'Franchise Report Filed Date',
                    type: 'date',
                    showWhen: (formData) => formData.return_includes_llc === '1' && formData.franchise_report
                },
                {
                    id: 'franchise_fee',
                    label: 'Franchise Report Fee',
                    type: 'number',
                    showWhen: (formData) => formData.return_includes_llc === '1' && formData.franchise_report
                },
                {
                    id: 'franchise_report_initials',
                    label: 'Franchise Report Initials',
                    type: 'text',
                    maxLength: 3,
                    showWhen: (formData) => formData.return_includes_llc === '1' && formData.franchise_report
                },
                {
                    id: 'franchise_notes',
                    label: 'Franchise Notes',
                    type: 'textarea',
                    rows: 4,
                    showWhen: (formData) => formData.return_includes_llc === '1' && formData.franchise_report
                },
            ],
            softwareUsed: [
                {
                    id: 'software_used',
                    label: 'Software Used',
                    type: 'select',
                    options: [
                        { value: '', label: 'Please Select' },
                        { value: 'Drake', label: 'Drake' },
                        { value: 'PTP', label: 'PTP' }
                    ]
                },

            ],

            paymentInformation: [
                {
                    id: 'payment_method',
                    label: 'Payment Method',
                    type: 'select',
                    required: true,
                    options: [
                        { value: '', label: 'Please Select' },
                        { value: 'EPS', label: 'EPS' },
                        { value: 'ACH', label: 'ACH' },
                        { value: 'Check', label: 'Check' },
                        { value: 'Cash', label: 'Cash' },
                        { value: 'Portal Payment', label: 'Portal Payment' },
                        { value: 'Square Credit Card', label: 'Square Credit Card' }
                    ],
                },
                {
                    id: 'payment_made',
                    label: 'Payment Made',
                    type: 'checkbox',
                    customClass: 'md:col-span-2',
                },
                {
                    id: 'payment_made_date',
                    label: 'Payment Date',
                    type: 'date',
                    showWhen: (formData) => formData.payment_made
                }
            ],

            completionStatus: [
                {
                    id: 'tax_complete',
                    label: 'Return Processing Completed',
                    type: 'checkbox',
                    customClass: 'md:col-span-2',
                },
                {
                    id: 'tax_complete_date',
                    label: 'Return Processing Completed Date',
                    type: 'date',
                    showWhen: (formData) => formData.tax_complete
                },
                {
                    id: 'tax_complete_initials',
                    label: 'Return Processing Completed Initials',
                    type: 'text',
                    maxLength: 3,
                    showWhen: (formData) => formData.tax_complete
                },
            ],

            additionalNotes: [
                {
                    id: 'note',
                    label: 'Notes',
                    type: 'textarea',
                    rows: 4
                }
            ],

        }
    }, [client, preparers]);

    const formatDateForInput = (dateString) => {
        if (!dateString) return '';

        // Handle different date formats
        let date;

        // Try parsing MM/DD/YYYY format
        if (dateString.includes('/')) {
            const [month, day, year] = dateString.split('/');
            date = new Date(year, month - 1, day);
        }
        // Try parsing ISO format (YYYY-MM-DDTHH:mm:ss.sssZ)
        else if (dateString.includes('T')) {
            date = new Date(dateString);
        }
        // Try parsing YYYY-MM-DD format
        else if (dateString.includes('-')) {
            date = new Date(dateString);
        } else {
            return '';
        }

        // Check if date is valid
        if (isNaN(date.getTime())) {
            return '';
        }

        // Format date as YYYY-MM-DD
        return date.toISOString().split('T')[0];
    };

    console.log(formFields);
    console.log(formData);

    const handleSubmit = async (e) => {
        e.preventDefault();

        setIsSaving(true);
        try {
            let response;
            if (mode === 'update') {
                response = await updateProfile(client.id, formData);
            } else {
                response = await createClient(formData);
            }

            if (response.success) {
                setIsSaving(false);
                await Swal.fire({
                                    title: 'Success!',
                                    text: response.message,
                                    icon: 'success',
                                    timer: 2000,
                                    showConfirmButton: true
                                });

                navigate(mode === 'update' ? `/client-progress/${client.id}${selectedYear ? `?year=${selectedYear}`:''}` : '/clients');
            }
        }
        catch (error) {
            setIsSaving(false);
            Swal.fire({
                          title: 'Error!',
                          text: error.message,
                          icon: 'error',
                          showConfirmButton: true
                      });
        }
    };

    const togglePasswordVisibility = (fieldId) => {
        setVisiblePasswords(prev => ({
            ...prev,
            [fieldId]: !prev[fieldId]
        }));
    };

    const renderField = (field) => {
        const baseInputStyles = `w-full rounded-lg border px-4 py-2
      bg-white dark:bg-gray-800
      border-gray-300 dark:border-gray-700
      text-gray-900 dark:text-white
      focus:outline-none focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-500
      focus:border-blue-600 dark:focus:border-blue-500
      transition-colors`;

        // Render error message if exists
        const errorMessage = validationErrors[field.id] && (
            <p className="mt-1 text-sm text-red-500">
                {validationErrors[field.id]}
            </p>
        );

        switch (field.type) {
            case 'date':
                return (
                    <DateInput field={field}
                               formData={formData}
                               setFormData={setFormData}
                               onChange={handleInputChange}
                               setIsSaving={setFormData}
                               error={errorMessage} />
                );
            case 'password':
                return (
                    <>
                        <div className="relative">
                            <input
                                type={visiblePasswords[field.id] ? 'text' : 'password'}
                                name={field.id}
                                value={formData[field.id] || ''}
                                onChange={handleInputChange}
                                required={field.required}
                                placeholder={field.placeholder}
                                minLength={field.minLength}
                                maxLength={field.maxLength}
                                className={`${baseInputStyles} pr-10`}
                            />
                            {field.showPasswordToggle && (
                                <button
                                    type="button"
                                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none"
                                    onClick={() => togglePasswordVisibility(field.id)}
                                    tabIndex="-1"
                                >
                                    {visiblePasswords[field.id] ? (
                                        <EyeOff className="w-4 h-4"/>
                                    ) : (
                                         <Eye className="w-4 h-4"/>
                                     )}
                                </button>
                            )}
                        </div>
                        {errorMessage}
                    </>
                );
            case 'checkbox':
                return (
                    <>
                        <label className="relative flex items-center cursor-pointer">
                            <input
                                type="checkbox"
                                name={field.id}
                                checked={formData[field.id] || false}
                                onChange={(e) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        [field.id]: e.target.checked
                                    }));
                                }}
                                className="peer sr-only"
                            />
                            <div className={`w-6 h-6 border-2 rounded-md
                border-gray-300 dark:border-gray-600
                bg-white dark:bg-gray-700
                peer-focus:ring-2 peer-focus:ring-blue-500
                peer-checked:bg-blue-600 peer-checked:border-blue-600
                flex items-center justify-center transition-all
                ${formData[field.id] ? 'bg-blue-600 border-blue-600' : ''}`}>
                                <svg
                                    className={`w-3.5 h-3.5 text-white ${formData[field.id] ? 'block' : 'hidden'}`}
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </div>
                            <span className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-200">
                {field.label}
            </span>
                        </label>
                        {errorMessage}
                    </>
                );
            case 'select':
                return (
                    <>
                        <select
                            name={field.id}
                            value={formData[field.id]}
                            onChange={handleInputChange}
                            required={field.required}
                            className={baseInputStyles}
                        >
                            {field.options.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        {errorMessage}
                    </>

                );

            case 'textarea':
                return (
                    <>
                        <textarea
                            name={field.id}
                            value={formData[field.id]}
                            onChange={handleInputChange}
                            required={field.required}
                            placeholder={field.placeholder}
                            rows={field.rows}
                            className={baseInputStyles}
                        />
                        {errorMessage}
                    </>

                );
            case 'setPassword':
                return (
                    <>
                        <div className="relative w-full">
                            <input
                                type={field.type}
                                name={field.id}
                                value={formData[field.id]}
                                onChange={handleInputChange}
                                required={field.required}
                                placeholder={field.placeholder}
                                minLength={field.minLength}
                                className={baseInputStyles}
                            />
                            {field.showPasswordToggle && (
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                                    onClick={() => {
                                        const input = document.querySelector(`input[name="${field.id}"]`);
                                        input.type = input.type === 'password' ? 'text' : 'password';
                                    }}
                                >
                                    <i className="fa fa-eye-slash"/>
                                </button>
                            )}
                            {mode === 'update' && (
                                <button onClick={handleSetClientPassword}
                                        className={`px-6 mt-2 py-2 rounded-lg font-medium bg-blue-600 hover:bg-blue-700 text-white
                                    ${isSaving ?
                                      'bg-blue-400 cursor-not-allowed animate-pulse' :
                                      'bg-blue-500 hover:bg-blue-600'
                                        }
                                    transition-colors`}>
                                    {isSaving ? 'Saving Password...' : 'Save Password'}
                                </button>
                            )}
                        </div>
                        {errorMessage}
                    </>
                );
            default:
                return (
                    <>
                        <div className="relative w-full">
                            <input
                                type={field.type}
                                name={field.id}
                                value={formData[field.id]}
                                onChange={handleInputChange}
                                required={field.required}
                                placeholder={field.placeholder}
                                minLength={field.minLength}
                                className={baseInputStyles}
                            />
                            {field.showPasswordToggle && (
                                <button
                                    type="button"
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                                    onClick={() => {
                                        const input = document.querySelector(`input[name="${field.id}"]`);
                                        input.type = input.type === 'password' ? 'text' : 'password';
                                    }}
                                >
                                    <i className="fa fa-eye-slash"/>
                                </button>
                            )}
                        </div>
                        {errorMessage}
                    </>
                );
        }
    };

    const shouldShowSection = (fields, formData) => {
        return fields.some(field => {
            // If field has no showWhen condition, it should be shown
            if (!field.showWhen) return true;
            // Otherwise, evaluate the showWhen condition
            return field.showWhen(formData);
        });
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center">
                    <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"/>
                    <div className="mt-2 text-gray-600 dark:text-gray-400">Loading...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="text-center text-red-600 dark:text-red-400">
                    Error: {error}
                </div>
            </div>
        );
    }

    if (!client) return null;

    if(print){
        return <PrintableForm uid={client.id} onPrintClose={handlePrintClose} type={'clientForm'} />
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 page-container z-10 relative">
            <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
                <div className="rounded-xl shadow-lg overflow-hidden bg-white dark:bg-gray-800">
                    {/* Header */}
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                        <div className="flex flex-row justify-between">
                            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                <div className="space-y-1">
                                    {client.is_portal_request ? (
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                            Portal Request
                                        </h3>
                                                                ): (
                                        <div className="flex flex-row justify-start items-center gap-2">
                                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white min-w-[140px]">
                                                {mode === 'update' ? 'Update' : 'Create New'} {client?.user_type === "account" ? "Account" : "Client"}

                                            </h3>
                                            <SelectTaxYear selectedYear={selectedYear}
                                                           availableYears={availableYears}
                                                           onSelectYear={setSelectedYear}/>
                                        </div>
                                    )}

                                    {mode === 'update' && (
                                        <Link to={`/client-progress/${client.id}${selectedYear ? `?year=${selectedYear}`:''}`} className="text-sm text-gray-600 dark:text-gray-400">
                                    {client?.profile.client}
                                        </Link>
                                    )}

                            </div>

                        </div>
                        <a
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600"
                                href="#"
                                onClick={handlePrint}
                            >
                                Print
                            </a>
                        </div>
                        {client.is_portal_request ? (
                            <PortalActions client={client} onRefresh={fetchData} className={`mt-2`}/>
                        ):(
                            mode === 'update' && (<ClientActions clientId={client.id} className={`mt-2`} year={selectedYear} />)
                        )}

                    </div>

                    {/* Content */}
                    <div className="p-6">
                        {client?.errors?.length > 0 && (
                            <div className="mb-6 rounded-lg border border-red-500 bg-red-50 dark:bg-red-900/20 p-4">
                                {client.errors.map((error, index) => (
                                    <div key={index} className="text-sm text-red-600 dark:text-red-400">
                                        {error}
                                    </div>
                                ))}
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="space-y-8">
                            {Object.entries(formFields).map(([section, fields]) => {
                                // Skip rendering the dependentFields section as it has a different structure
                                if (section === 'dependentFields') return null;

                                // Check if fields is an array before processing
                                if (!Array.isArray(fields)) return null;

                                // Check if any fields in this section should be shown
                                if (!shouldShowSection(fields, formData)) return null;

                                // Filter out fields that shouldn't be shown
                                const visibleFields = fields.filter(field =>
                                                                        !field.showWhen || field.showWhen(formData)
                                );

                                // If no fields are visible, don't render the section
                                if (visibleFields.length === 0) return null;

                                return (
                                    <div key={section} className="space-y-6">
                                        <h4 className="text-lg font-bold font-medium border-b pb-2 text-gray-900 dark:text-white border-gray-200 dark:border-gray-700 capitalize">
                                            {section.replace(/([A-Z])/g, ' $1').trim()}
                                        </h4>

                                        <div className="grid gap-6 md:grid-cols-2">
                                            {visibleFields.map(field => (
                                                <div key={field.id} className={`${field.customClass ? field.customClass:''} space-y-2`}>
                                                    {field.type !== 'checkbox' && (
                                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                                                            {field.label}
                                                            {field.required && (
                                                                <span className="text-red-500 ml-1">*</span>
                                                            )}
                                                        </label>
                                                    )}

                                                    {renderField(field)}

                                                    {field.id === 'dependents' && (
                                                        <>
                                                            {/* Render dependent fields separately if needed */}
                                                            {formData.dependents === 'have_dependents' && formFields.dependentFields && (
                                                                <div className="space-y-6">
                                                                    {formData.dependents === 'have_dependents' && (
                                                                        <div className="space-y-6">
                                                                            <DependentFields formData={formData} setFormData={setFormData}/>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}


                            <div className="flex justify-end space-x-4 pt-6">
                                <button
                                    type="submit"
                                    disabled={isSaving}
                                    className={`px-6 py-2 rounded-lg font-medium bg-blue-600 hover:bg-blue-700 text-white
                                    ${isSaving ?
                                      'bg-blue-400 cursor-not-allowed animate-pulse' :
                                      'bg-blue-500 hover:bg-blue-600'
                                    }
                                    transition-colors`}
                                >
                                    {isSaving ? (
                                        <span>
                                        Processing...
                                        </span>
                                    ) : (
                                         <span>{mode === 'update' ? 'Update' : 'Create'} {client?.user_type === "account" ? "Account" : "Client"}</span>
                                     )}

                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ClientForm;
